import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from "react";
import axios from "axios";
import api from "@/lib/api";

interface AuthContextType {
    isAuthenticated: boolean;
    isLoading: boolean;
    refresh: () => Promise<void>;
    logout: () => void;
    user: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

let isRefreshing = false;

export function AuthProvider({ children }: { children: ReactNode }) {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [user, setUser] = useState<string | null>(null);

    // Clean up any old sessionStorage flags from previous versions
    useEffect(() => {
        sessionStorage.removeItem('auth_check_in_progress');
    }, []);

    const refresh = async () => {
        // Prevent concurrent refreshes within the same page lifecycle
        if (isRefreshing) {
            return;
        }

        const path = window.location.pathname;
        const isLoginPage = path === '/login' || path === '/login/' || path === '/' || path === '/index.html';

        if (import.meta.env.DEV) {
            const devEmail = localStorage.getItem("dev_user_email") || "dev@hubinternational.com";
            setIsAuthenticated(true);
            setUser(devEmail);
            setIsLoading(false);
            return;
        }

        // On login page, we start as unauthenticated but still need to set isLoading(false)
        if (isLoginPage) {
            setIsAuthenticated(false);
            setUser(null);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        isRefreshing = true;
        try {
            const me = await api.getMe();

            if (me.authenticated) {
                setIsAuthenticated(true);
                setUser(me.email);
            } else {
                console.warn("[Auth] Backend reported unauthenticated");
                setIsAuthenticated(false);
                setUser(null);
            }
        } catch (error: any) {
            // Handle specific errors that should not trigger a logout/redirect loop
            const isNetworkError = error?.code === 'ERR_NETWORK' || error?.message?.includes('Network');
            const isCanceled = axios.isCancel(error);

            if (isNetworkError) {
                console.warn("[Auth] Network error during auth check, likely Cloudflare Access redirect in progress");
                // Don't change isAuthenticated state immediately, let the redirect happen or retry
            } else if (!isCanceled) {
                console.error("[Auth] Unexpected authentication error:", error);
                setIsAuthenticated(false);
                setUser(null);
            }
        } finally {
            isRefreshing = false;
            setIsLoading(false);
        }
    };

    const logout = () => {
        if (import.meta.env.DEV) {
            localStorage.removeItem("dev_user_email");
            // Force clear any other local flags if used
        }
        // In production/Zero Trust, hitting the logout endpoint is handled by the button or separate logic, 
        // but updating local state is good UI practice.
        setIsAuthenticated(false);
        setUser(null);
        // Redirect to CF logout if needed, but for now we just clear local state 
        // effectively showing the "Login" (or "Access Denied") page.
    };

    useEffect(() => {
        void refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const value = useMemo(
        () => ({ isAuthenticated, isLoading, refresh, logout, user }),
        [isAuthenticated, isLoading, user]
    );

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
}
